/**
 * Listener effects for the SMM WS channel (assistant-smm `/api/ws`).
 *
 * assistant-smm pushes lightweight `Invalidate` frames whenever a social post
 * changes, both from API mutations and from the background publisher moving a
 * post through SCHEDULED → PUBLISHING → PUBLISHED/PARTIAL/FAILED (SKY-4777).
 * These effects:
 * 1. keep the socket connected only on `/admin/smm/*` pages and only for users
 *    allowed `read:admin:smm` — the only place these updates are consumed,
 * 2. authenticate the socket on open, and re-authenticate on token refresh,
 * 3. map `social-posts` frames to `SocialPost` tag invalidations, so
 *    subscribed queries refetch over the Cerbos-checked HTTP API (no post data
 *    crosses the socket),
 * 4. refetch every `SocialPost` query when the socket opens again, since the
 *    frames sent while it was closed are lost (see {@link createReopenCatchUp}).
 *
 * Heartbeat: assistant-smm uses the same protocol as assistant-api — the
 * server sends protocol-level pings the browser answers automatically, and
 * ws-core's `reconnectOnClose` recovers dropped sockets. See the pulse notes
 * in `ws-assistant-effects.ts` for the optional client-side liveness probe.
 */

import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import type { AppDispatch, RootState } from 'src/store';
import {
  actionSmmOnOpen,
  actionSmmOnMessage,
  parseInvalidateMessage,
  selectWsSmmStatus,
  subscribeWsSmmData,
  wsSmmConnect,
  wsSmmDisconnect,
  wsSmmSend,
} from '@skynet/data-ws-smm';
import type { InvalidateMessage, SmmRealtimeActionFor } from '@skynet/data-ws-smm';
import { LIST_TAG_ID } from '@skynet/data-helpers';
import { selectAccessToken, setAccessToken } from '@skynet/data-auth-api';
import { smmApi } from '@skynet/data-smm';
import { SMM_POSTS_URL, smmConfig } from '@skynet/smm-feature';
import { selectNavigation } from 'src/store/navigation/selectors';
import { routerHistoryChange } from 'src/store/router/actions';
import { debounceInvalidate } from './ws-invalidation-debounce';
import type { MappedInvalidations } from './ws-invalidation-debounce';
import { createReopenCatchUp } from './ws-reopen-catch-up';

/** Navigation permission required to open the SMM socket: the one that gates the SMM pages. */
export const SMM_WS_PERMISSION = smmConfig.cerbosAction;

/** Pages that consume SMM realtime updates. */
const SMM_PATH_PREFIX = SMM_POSTS_URL;

/**
 * Which caches each `social-posts` action touches: `'list'` when the post is
 * gone (its detail query would 404), `'post-and-list'` otherwise. Mirrors the
 * mutations' `invalidatesTags` in `@skynet/data-smm` — a new post has no
 * detail query yet, and a cancelled post stays readable (status CANCELLED), so
 * it refetches too.
 *
 * A `Record` over the catalogue, so a new backend action fails to compile until
 * it's classified here.
 */
const SOCIAL_POST_INVALIDATION: Record<
  SmmRealtimeActionFor<'social-posts'>,
  'list' | 'post-and-list'
> = {
  created: 'list',
  updated: 'post-and-list',
  deleted: 'list',
  scheduled: 'post-and-list',
  unscheduled: 'post-and-list',
  redrafted: 'post-and-list',
  cancelled: 'post-and-list',
  publishing: 'post-and-list',
  published: 'post-and-list',
  partial: 'post-and-list',
  failed: 'post-and-list',
  // Kept as a record (status UNPUBLISHED), like a cancelled post.
  unpublished: 'post-and-list',
};

const isCataloguedSocialPostAction = (
  action: string,
): action is SmmRealtimeActionFor<'social-posts'> =>
  Object.hasOwn(SOCIAL_POST_INVALIDATION, action);

/**
 * The disconnect started by the last sync, if still running. `wsSmmDisconnect`
 * waits ~100ms before closing while the status still reads `connected`, so a
 * quick navigate-away-and-back must wait for it instead of treating the socket
 * as up.
 */
let pendingDisconnect: Promise<unknown> | null = null;

/**
 * Whether the last sync wanted the socket open. Lets the open handler drop a
 * socket that opened after it stopped being wanted. Permissions only change
 * on navigation or a tenant switch, both of which re-sync, so the stored
 * decision stays current. Starts `false`: the socket is only ever connected
 * by a sync.
 */
let isWsSmmWanted = false;

/**
 * Refetches every `SocialPost` query when a wanted socket opens again, be it
 * ws-core's auto-reconnect or a fresh connect after coming back to the SMM
 * pages. Tracks the SMM cache's lifetime, so the first open after a sign-out
 * or tenant switch (both reset `smmApi`) is not taken for a reopen.
 *
 * Whole-type on purpose: the frames missed are unknown, so it cannot apply
 * {@link SOCIAL_POST_INVALIDATION}'s list-only rule — a post deleted meanwhile
 * refetches its detail query, which the post page renders as a 404.
 */
const reopenCatchUp = createReopenCatchUp({
  catchUp: () => [smmApi.util.invalidateTags(['SocialPost'])],
  isCacheReset: smmApi.util.resetApiState.match,
  isOpen: (state) => selectWsSmmStatus(state) === 'connected',
});

/**
 * Disconnects the socket unless a disconnect is already in flight — two
 * overlapping `wsSmmDisconnect` thunks would both see `connected` during its
 * delay and close twice.
 */
const disconnectOnce = async (dispatch: AppDispatch): Promise<void> => {
  if (pendingDisconnect) {
    return;
  }

  const disconnecting = dispatch(wsSmmDisconnect());
  pendingDisconnect = disconnecting;

  try {
    await disconnecting;
  } finally {
    if (pendingDisconnect === disconnecting) {
      pendingDisconnect = null;
    }
  }
};

/** Test helper: clears the module-level sync state between test cases. */
export const resetWsSmmSyncState = () => {
  pendingDisconnect = null;
  isWsSmmWanted = false;
  reopenCatchUp.reset();
};

/**
 * Whether the SMM socket should be open for this page and principal: on
 * `/admin/smm` or any page under it, with `read:admin:smm` granted.
 */
export const shouldConnectWsSmm = (
  pathname: string,
  allowedNavActions: readonly string[] | null | undefined,
): boolean =>
  (pathname === SMM_PATH_PREFIX || pathname.startsWith(`${SMM_PATH_PREFIX}/`)) &&
  (allowedNavActions ?? []).includes(SMM_WS_PERMISSION);

/** Store access {@link syncWsSmmConnection} needs. */
export interface WsSmmSyncApi {
  dispatch: AppDispatch;
  getState: () => RootState;
  /** Resolves once the socket has left the `disconnecting` state. */
  waitUntilNotDisconnecting: () => Promise<unknown>;
}

/**
 * Connects or disconnects the SMM socket so its state matches
 * {@link shouldConnectWsSmm}. Safe to call repeatedly:
 * - connect wanted while a disconnect is in flight: waits for the socket to
 *   close, then reconnects;
 * - disconnect wanted while connecting: left to the open handler, which drops
 *   the socket as soon as it opens.
 */
export const syncWsSmmConnection = async (
  api: WsSmmSyncApi,
  pathname: string,
  allowedNavActions: readonly string[] | null | undefined,
): Promise<void> => {
  const { dispatch, getState } = api;
  isWsSmmWanted = shouldConnectWsSmm(pathname, allowedNavActions);

  if (!isWsSmmWanted) {
    if (selectWsSmmStatus(getState()) === 'connected') {
      await disconnectOnce(dispatch);
    }
    return;
  }

  if (pendingDisconnect) {
    await pendingDisconnect;
  }

  if (selectWsSmmStatus(getState()) === 'disconnecting') {
    await api.waitUntilNotDisconnecting();
  }

  // subscribeWsSmmData only connects from `idle`, so this is a no-op while
  // connected or connecting
  await dispatch(subscribeWsSmmData(wsSmmConnect()));
};

/**
 * {@link WsSmmSyncApi} for code holding the store directly (route loaders).
 */
export const wsSmmSyncApiFor = (store: {
  dispatch: AppDispatch;
  getState: () => RootState;
  subscribe: (listener: () => void) => () => void;
}): WsSmmSyncApi => ({
  dispatch: store.dispatch,
  getState: store.getState,
  waitUntilNotDisconnecting: async () => {
    await new Promise<void>((resolve) => {
      const check = () => {
        if (selectWsSmmStatus(store.getState()) !== 'disconnecting') {
          unsubscribe();
          resolve();
        }
      };
      const unsubscribe = store.subscribe(check);
      check();
    });
  },
});

/**
 * Maps an `Invalidate` frame to the RTK Query invalidation actions to
 * dispatch. SMM frames map to cache invalidations only — nothing here is
 * stateful, so everything is safe to replay on the debounce's trailing edge
 * (see {@link MappedInvalidations}). Unknown entities map to no actions
 * (forward-compatible with new backend events).
 */
export const invalidateSmmActionsFor = (msg: InvalidateMessage): MappedInvalidations => {
  const { entity, action, id } = msg;

  switch (entity) {
    case 'social-posts':
      if (id === undefined) {
        return { cache: [smmApi.util.invalidateTags(['SocialPost'])] };
      }

      // Uncatalogued (future) actions refetch both, the safe default
      if (isCataloguedSocialPostAction(action) && SOCIAL_POST_INVALIDATION[action] === 'list') {
        return { cache: [smmApi.util.invalidateTags([{ type: 'SocialPost', id: LIST_TAG_ID }])] };
      }

      return {
        cache: [
          smmApi.util.invalidateTags([
            { type: 'SocialPost', id },
            { type: 'SocialPost', id: LIST_TAG_ID },
          ]),
        ],
      };

    // Unknown/future entities: no-op (forward-compatible with new backend events)
    default:
      return { cache: [] };
  }
};

export const startWsSmmEffects = () => {
  // Authenticate as soon as the socket opens (and on every reconnect) with the
  // current access token. On auth failure the server closes with 4401 and
  // reconnectOnClose re-runs this with whatever token is in the store then.
  const authOnOpenAbort = startAppListening({
    actionCreator: actionSmmOnOpen,
    effect: async (_action, listenerApi) => {
      const token = selectAccessToken(listenerApi.getState());

      // The socket can open when it is no longer wanted: ws-core's
      // reconnectOnClose keeps retrying after an unclean close, even while
      // the slice reads `idle` (a failed attempt sets it), so a reconnect can
      // land after the user left /admin/smm, lost access, or signed out. The
      // same check drops a socket that was still connecting when the user
      // navigated away
      if (!token || !isWsSmmWanted) {
        await disconnectOnce(listenerApi.dispatch);
        return;
      }

      listenerApi.dispatch(wsSmmSend({ command: 'Auth', token }));
      // Catch up on what the socket missed while it was closed
      reopenCatchUp.onOpen(listenerApi.dispatch);
    },
  });

  // Re-authenticate an open socket after a token refresh, so the server
  // session doesn't outlive the old token
  const authOnRefreshAbort = startAppListening({
    actionCreator: setAccessToken,
    effect: (action, listenerApi) => {
      const token = action.payload.accessToken;

      if (token && selectWsSmmStatus(listenerApi.getState()) === 'connected') {
        listenerApi.dispatch(wsSmmSend({ command: 'Auth', token }));
      }
    },
  });

  // Keep the socket scoped to the SMM pages while navigating inside a domain,
  // where the domain loader does not re-run
  const connectOnNavigationAbort = startAppListening({
    actionCreator: routerHistoryChange,
    effect: async (action, listenerApi) => {
      // Only the latest navigation matters: cancels a previous sync still
      // waiting for the socket to settle
      listenerApi.cancelActiveListeners();

      await syncWsSmmConnection(
        {
          dispatch: listenerApi.dispatch,
          getState: () => listenerApi.getState(),
          waitUntilNotDisconnecting: async () => {
            await listenerApi.condition(
              (_action, state) => selectWsSmmStatus(state) !== 'disconnecting',
            );
          },
        },
        action.payload.path,
        selectNavigation(listenerApi.getState()),
      );
    },
  });

  const reopenCatchUpAbort = reopenCatchUp.startListening();

  const invalidateOnMessageAbort = startAppListening({
    actionCreator: actionSmmOnMessage,
    effect: (action, listenerApi) => {
      const msg = parseInvalidateMessage(action.payload);

      if (!msg) {
        return;
      }

      debounceInvalidate('smm', msg, invalidateSmmActionsFor(msg), listenerApi.dispatch);
    },
  });

  return () => {
    authOnOpenAbort();
    authOnRefreshAbort();
    connectOnNavigationAbort();
    reopenCatchUpAbort();
    invalidateOnMessageAbort();
  };
};
